'use client';

import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';

/** A small browser boundary: CMS content stays rendered on the server. */
export function HeroCarMotion({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const car = ref.current;
    if (!car) return;
    const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
    let dispose = () => {};
    const configure = () => {
      dispose();
      car.removeAttribute('style');
      if (preference.matches) return;
      let frame = 0;
      let progress = window.scrollY;
      let origin = car.getBoundingClientRect();
      const measure = () => {
        car.removeAttribute('style');
        origin = car.getBoundingClientRect();
      };
      measure();
      const render = () => {
        const target = Math.max(0, window.scrollY);
        progress += (target - progress) * 0.14;
        if (Math.abs(target - progress) < 0.1) progress = target;
        // Depart relative to the hero, so normal page scrolling carries
        // the taxi away with its surrounding content.
        const distance = Math.min(
          900,
          Math.max(580, window.innerHeight * 0.95),
        );
        const travel = Math.min(1, progress / distance);
        const eased = travel * travel * (3 - 2 * travel);
        const scale = 1 - 0.16 * eased;
        const x =
          (window.innerWidth - origin.left + origin.width * 0.15) * eased;
        const y = Math.sin(Math.PI * eased) * 8;
        // Fade from the first scroll pixels, along the entire departure.
        const opacity = (1 - travel) ** 1.35;
        car.style.transform = `translate3d(${x}px, ${y}px, 0) scale(${scale})`;
        car.style.opacity = `${opacity}`;
        car.style.visibility = travel === 1 ? 'hidden' : 'visible';
        frame = progress !== target ? requestAnimationFrame(render) : 0;
      };
      const update = () => {
        if (!frame) frame = requestAnimationFrame(render);
      };
      const resize = () => {
        measure();
        update();
      };
      window.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', resize);
      update();
      dispose = () => {
        cancelAnimationFrame(frame);
        window.removeEventListener('scroll', update);
        window.removeEventListener('resize', resize);
      };
    };
    configure();
    preference.addEventListener('change', configure);
    return () => {
      dispose();
      preference.removeEventListener('change', configure);
      car.removeAttribute('style');
    };
  }, []);
  return (
    <div ref={ref} className="hero-car">
      {children}
    </div>
  );
}

export function ScrollReveals() {
  useEffect(() => {
    if (!('IntersectionObserver' in window)) return;
    const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
    let dispose = () => {};
    const configure = () => {
      dispose();
      if (preference.matches || !('IntersectionObserver' in window)) return;
      const elements = Array.from(
        document.querySelectorAll<HTMLElement>(
          '.section-heading, .service-article, .booking-feature, .vehicle-intro, .vehicle-photo, .vehicle-detail-photo, .vehicle-trait, .destination-photo, .destination-details, .footer-top, .footer-grid, .footer-location',
        ),
      );
      const observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            entry.target.classList.add('is-revealed');
            observer.unobserve(entry.target);
          }
        },
        { threshold: 0.08, rootMargin: '0px 0px -30px 0px' },
      );
      for (const element of elements) {
        // Already visible content never flashes away when hydration finishes.
        if (element.getBoundingClientRect().top < window.innerHeight - 30)
          continue;
        element.classList.add('scroll-reveal');
        const siblings = Array.from(element.parentElement?.children || []);
        element.style.setProperty(
          '--reveal-delay',
          `${Math.min(siblings.indexOf(element), 2) * 70}ms`,
        );
        observer.observe(element);
      }
      dispose = () => {
        observer.disconnect();
        for (const element of elements) {
          element.classList.remove('scroll-reveal', 'is-revealed');
          element.style.removeProperty('--reveal-delay');
        }
      };
    };
    configure();
    preference.addEventListener('change', configure);
    return () => {
      dispose();
      preference.removeEventListener('change', configure);
    };
  }, []);
  return null;
}
